import * as React from 'react';
import moment from 'moment';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from '@common/bw-ui/ui/alert-dialog';
import { Button } from '@common/bw-ui/ui/button';
import { Alert } from '@common/bw-ui/ui/alert';
import { Spinner } from '@common/bw-ui/ui/spinner';
import { CheckCircleIcon } from '@common/bw-ui/icons';
import { cn } from '@common/bw-ui/lib/utils';

export interface CancelBookingDialogProps {
  open: boolean;
  booking: {
    resourceName: string;
    fromTime: string;
    toTime: string;
    resourceImageUrl?: string;
  };
  cancellationFee: number | null;
  currencyCode: string;
  isLoadingFee: boolean;
  isDeleting: boolean;
  isComplete: boolean;
  onKeepBooking: () => void;
  onCancelBooking: () => void;
  onClose: () => void;
}

function formatFee(amount: number, currencyCode: string): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currencyCode,
  }).format(amount);
}

function SkeletonBar({ className }: { className?: string }) {
  return (
    <div
      className={cn('tw:animate-pulse tw:rounded-md tw:bg-black/10', className)}
    />
  );
}

function LoadingState() {
  return (
    <>
      <AlertDialogHeader>
        <AlertDialogTitle>Cancel this booking?</AlertDialogTitle>
        <AlertDialogDescription className="tw:flex tw:flex-col tw:gap-2 tw:pt-1">
          <SkeletonBar className="tw:h-4 tw:w-full" />
          <SkeletonBar className="tw:h-4 tw:w-3/4" />
          <SkeletonBar className="tw:h-4 tw:w-1/3 tw:mt-2" />
        </AlertDialogDescription>
      </AlertDialogHeader>

      <AlertDialogFooter className="tw:gap-2">
        <SkeletonBar className="tw:h-10 tw:w-32 tw:rounded-md" />
        <SkeletonBar className="tw:h-10 tw:w-36 tw:rounded-md" />
      </AlertDialogFooter>
    </>
  );
}

function ConfirmState({
  booking,
  cancellationFee,
  currencyCode,
  isDeleting,
  onKeepBooking,
  onCancelBooking,
}: Pick<
  CancelBookingDialogProps,
  | 'booking'
  | 'cancellationFee'
  | 'currencyCode'
  | 'isDeleting'
  | 'onKeepBooking'
  | 'onCancelBooking'
>) {
  const fromMoment = moment(booking.fromTime);
  const toMoment = moment(booking.toTime);
  const date = fromMoment.format('dddd, D MMMM YYYY');
  const time = `${fromMoment.format('h:mm A')} – ${toMoment.format('h:mm A')}`;

  return (
    <>
      <AlertDialogHeader>
        <AlertDialogTitle>Cancel this booking?</AlertDialogTitle>
        <AlertDialogDescription>
          Are you sure you want to cancel your booking for{' '}
          <span className="tw:font-medium tw:text-foreground">
            {booking.resourceName}
          </span>{' '}
          on <span className="tw:font-medium tw:text-foreground">{date}</span>,{' '}
          <span className="tw:font-medium tw:text-foreground">{time}</span>?
          <p>This action cannot be undone.</p>
        </AlertDialogDescription>
      </AlertDialogHeader>

      {cancellationFee != null && cancellationFee > 0 && (
        <Alert
          variant="warning"
          title="Cancellation fee"
          description={`A fee of ${formatFee(cancellationFee, currencyCode)} will be charged.`}
        />
      )}

      <AlertDialogFooter className="tw:gap-2">
        <Button variant="outline" onClick={onKeepBooking} disabled={isDeleting}>
          Keep Booking
        </Button>
        <Button
          variant="destructive"
          onClick={onCancelBooking}
          disabled={isDeleting}
        >
          {isDeleting && <Spinner className="tw:size-4" />}
          Cancel Booking
        </Button>
      </AlertDialogFooter>
    </>
  );
}

function ConfirmedState({
  booking,
  onClose,
}: {
  booking: CancelBookingDialogProps['booking'];
  onClose: () => void;
}) {
  const fromMoment = moment(booking.fromTime);
  const toMoment = moment(booking.toTime);
  const date = fromMoment.format('dddd, D MMMM YYYY');
  const time = `${fromMoment.format('h:mm A')} – ${toMoment.format('h:mm A')}`;

  return (
    <div className="tw:flex tw:flex-col tw:items-center tw:gap-6 tw:py-4">
      <AlertDialogTitle className="tw:sr-only">
        Cancellation complete
      </AlertDialogTitle>
      <AlertDialogDescription className="tw:sr-only">
        Your booking has been cancelled successfully.
      </AlertDialogDescription>
      <div
        data-slot="icon"
        className="tw:flex tw:items-center tw:justify-center tw:w-20 tw:h-20 tw:rounded-full tw:bg-success/10"
      >
        <CheckCircleIcon
          className="tw:w-12 tw:h-12 tw:text-success"
          aria-hidden="true"
        />
      </div>

      <div className="tw:flex tw:flex-col tw:gap-2 tw:text-center">
        <h2 className="tw:text-2xl tw:font-normal tw:text-foreground">
          Booking cancelled
        </h2>
        <p className="tw:text-base tw:text-muted">
          You&apos;ll receive a confirmation email shortly.
        </p>
      </div>

      <div
        data-slot="booking-details"
        className={cn(
          'tw:rounded-lg tw:bg-secondary tw:p-4 tw:flex tw:gap-4 tw:w-full tw:text-left tw:items-center',
        )}
      >
        {booking.resourceImageUrl && (
          <img
            data-slot="image"
            src={booking.resourceImageUrl}
            alt={booking.resourceName}
            className="tw:w-16 tw:h-16 tw:rounded-md tw:object-cover tw:shrink-0"
          />
        )}
        <div className="tw:flex tw:flex-col tw:gap-1">
          <span
            data-slot="resource-name"
            className="tw:text-sm tw:font-medium tw:text-foreground"
          >
            {booking.resourceName}
          </span>
          <span data-slot="date" className="tw:text-sm tw:text-muted">
            {date}
          </span>
          <span data-slot="time" className="tw:text-sm tw:text-muted">
            {time}
          </span>
        </div>
      </div>

      <Button onClick={onClose} className="tw:w-full">
        Close
      </Button>
    </div>
  );
}

export function CancelBookingDialog({
  open,
  booking,
  cancellationFee,
  currencyCode,
  isLoadingFee,
  isDeleting,
  isComplete,
  onKeepBooking,
  onCancelBooking,
  onClose,
}: CancelBookingDialogProps) {
  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen || isDeleting || isLoadingFee) return;
    if (isComplete) {
      onClose();
    } else {
      onKeepBooking();
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className={cn(isComplete && 'tw:text-center')}>
        {isComplete ? (
          <ConfirmedState booking={booking} onClose={onClose} />
        ) : isLoadingFee ? (
          <LoadingState />
        ) : (
          <ConfirmState
            booking={booking}
            cancellationFee={cancellationFee}
            currencyCode={currencyCode}
            isDeleting={isDeleting}
            onKeepBooking={onKeepBooking}
            onCancelBooking={onCancelBooking}
          />
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}
